// TODO: vitest component test (pages are covered by Playwright e2e)
import { useEffect, useState, useMemo, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, FormActions, Input, Select, Textarea, SearchableDropdown, FileUpload } from '@/components/ui'
import { api } from '@/lib/api'
import { utcDateOnly } from '@/lib/date'
import {
  useWorkOrderDetail,
  useCreateWorkOrder,
  useUpdateWorkOrder,
  useEquipment,
  useVendors,
} from '@/hooks/useCmms'
import { useUsers } from '@/hooks/useUsers'
import { useMaintenanceRequests } from '@/hooks/useMaintenanceRequests'
import { useLeases, useLease } from '@/hooks/useLeases'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useFeatureFlags } from '@/lib/featureFlags'
import type { Equipment, Vendor, Lease } from '@/types'

async function uploadWorkOrderImage(file: File): Promise<string> {
  const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
    content_type: file.type,
  })
  await fetch(presigned.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  })
  return `/api/images/${presigned.key}`
}

interface WorkOrderFormData {
  title: string
  description: string
  priority: string
  status: string
  equipment_id: string
  lease_id: string
  assigned_to_id: string
  maintenance_request_id: string
  due_date: string
  estimated_cost: string
  actual_cost: string
  notes: string
}

export function WorkOrderFormPage() {
  const { t } = useTranslation('cmms')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id
  const workOrderId = Number(id)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Deep-link from the lease deposit-settlement card (T130-012):
  // /work-orders/new?lease_id={id}. Only ever hits the create route, but the
  // param is read unconditionally — nothing downstream assumes create-only.
  const leaseIdParam = searchParams.get('lease_id')
  const deepLinkedLeaseId = leaseIdParam ? Number(leaseIdParam) : undefined

  const { data: existingWorkOrder, isLoading: isLoadingWorkOrder } = useWorkOrderDetail(
    isEditing ? workOrderId : 0
  )
  const createWorkOrder = useCreateWorkOrder()
  const updateWorkOrder = useUpdateWorkOrder()
  const { data: usersData } = useUsers()
  const { data: maintenanceData } = useMaintenanceRequests(1, 'open')
  const { data: accountSettings } = useAccountSettings()
  const featureFlags = useFeatureFlags()

  // The lease picker is a CMMS-module extra (move-out deductions), not a
  // core work-order field — hide it for accounts that haven't turned CMMS
  // on UNLESS we arrived via the settlement-card deep link, in which case
  // the lease is the whole point of the visit and must stay visible+locked
  // regardless of the module setting. The `/work-orders/*` routes already
  // gate on the Unleash `cmms` flag (see App.tsx), so this is effectively
  // just the per-account `cmms_enabled` setting — the flag check is kept
  // for parity with the other cmms-gated surfaces (e.g. LeaseDetailPage).
  const cmmsModuleEnabled = featureFlags.cmms && (accountSettings?.settings?.cmms_enabled ?? false)
  const showLeasePicker = cmmsModuleEnabled || !!deepLinkedLeaseId

  // Wrap the `?? ||` logical expressions in their own useMemo so the references
  // are stable across renders (deps of subsequent useMemo blocks).
  const users = useMemo(() => usersData?.data ?? [], [usersData])
  const maintenanceRequests = useMemo(() => maintenanceData?.data ?? [], [maintenanceData])

  // Assignee picker: ONE combined dropdown, ONE search box, but the two halves
  // it merges are fetched completely differently — spelled out here because
  // that's exactly what made this picker look server-searched while actually
  // being a client-side .filter() over both.
  //
  // - VENDORS are Meilisearch-indexed (Vendor.search_for_account) and are
  //   genuinely searched + paged on the server below via useVendors' `q`/`page`.
  // - USERS (staff accounts) are NOT indexed and /api/users has no pagination
  //   or search param at all (Api::UsersController#index always returns the
  //   full account list) — indexing User would be net-new scope beyond this
  //   epic, so users stay a small bounded list, filtered client-side by the
  //   same search text. This is the BA-resolved judgement call for T015.
  const [assigneeSearch, setAssigneeSearch] = useState('')
  const [vendorPage, setVendorPage] = useState(1)
  const [accumulatedVendors, setAccumulatedVendors] = useState<Vendor[]>([])

  const { data: vendorsData, isLoading: isLoadingVendors } = useVendors(vendorPage, {
    active: true,
    q: assigneeSearch || undefined,
  })

  // A new query starts a new list; anything else appends the next page.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset vendor pagination state when the search query changes (same idiom as the equipment picker below)
    setVendorPage(1)
    setAccumulatedVendors([])
  }, [assigneeSearch])

  useEffect(() => {
    if (!vendorsData?.data) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: accumulate paginated TanStack Query results across fetches (external system, not derivable from a single render)
    setAccumulatedVendors((prev) =>
      vendorPage === 1
        ? vendorsData.data
        : [...prev, ...vendorsData.data.filter((v) => !prev.some((p) => p.id === v.id))]
    )
  }, [vendorsData, vendorPage])

  // Equipment picker: searched and paged on the server (SearchableDropdown's
  // async contract), not bulk-loaded. useEquipment(1, {}) fetched a hard 20-row
  // page with no search — row 21 onward was unreachable on any account with
  // more equipment than that.
  const [equipmentSearch, setEquipmentSearch] = useState('')
  const [equipmentPage, setEquipmentPage] = useState(1)
  const [accumulatedEquipment, setAccumulatedEquipment] = useState<Equipment[]>([])

  const { data: equipmentData, isLoading: isLoadingEquipment } = useEquipment(equipmentPage, {
    q: equipmentSearch || undefined,
  })

  // A new query starts a new list; anything else appends the next page.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset local pagination state when the search query changes (same idiom as ParcelFormPage/AnnouncementFormPage)
    setEquipmentPage(1)
    setAccumulatedEquipment([])
  }, [equipmentSearch])

  useEffect(() => {
    if (!equipmentData?.data) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: accumulate paginated TanStack Query results across fetches (external system, not derivable from a single render)
    setAccumulatedEquipment((prev) =>
      equipmentPage === 1
        ? equipmentData.data
        : [...prev, ...equipmentData.data.filter((e) => !prev.some((p) => p.id === e.id))]
    )
  }, [equipmentData, equipmentPage])

  // Lease picker: same server-search + paging contract as Equipment above.
  // "ended-or-ending" per the design spec has no direct server filter (a
  // RentalLease's `ended_at` is an optional term-end date independent of
  // `status` — see api/app/models/rental_lease.rb — so there's no
  // "ended_at present" query param). The closest available proxy is
  // status=active,ended: excludes draft/pending_start/pending_signatures
  // (never started, so never "ending") and reaches every lease the
  // settlement-card deep link can point at. It over-includes ordinary
  // open-ended active leases with no end date at all, which the search box
  // covers in practice (the settlement flow always names a specific lease).
  const [leaseSearch, setLeaseSearch] = useState('')
  const [leasePage, setLeasePage] = useState(1)
  const [accumulatedLeases, setAccumulatedLeases] = useState<Lease[]>([])

  const { data: leasesData, isLoading: isLoadingLeases } = useLeases(
    leasePage,
    'active,ended',
    showLeasePicker,
    leaseSearch || undefined,
  )

  // A new query starts a new list; anything else appends the next page.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset local pagination state when the search query changes (same idiom as the equipment picker above)
    setLeasePage(1)
    setAccumulatedLeases([])
  }, [leaseSearch])

  useEffect(() => {
    if (!leasesData?.data) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: accumulate paginated TanStack Query results across fetches (external system, not derivable from a single render)
    setAccumulatedLeases((prev) =>
      leasePage === 1
        ? leasesData.data
        : [...prev, ...leasesData.data.filter((l) => !prev.some((p) => p.id === l.id))]
    )
  }, [leasesData, leasePage])

  // Deep-link fetch: the settlement card can point at a lease outside the
  // ended-or-ending picker page (e.g. the picker hasn't loaded it, or the
  // search box is empty) — fetch it directly so the locked field always has
  // a real label instead of falling back to the placeholder.
  const { data: deepLinkedLease } = useLease(deepLinkedLeaseId ?? 0, !!deepLinkedLeaseId)

  // Image upload
  const [localImageUrls, setImageUrls] = useState<string[] | null>(null)
  const imageUrls = localImageUrls ?? (isEditing && existingWorkOrder ? (existingWorkOrder.image_urls ?? []) : [])

  // Combined assignee options: users (client-filtered, bounded list) + vendors
  // (server-searched, already filtered by `q` above — do NOT filter them again
  // here, that would hide vendor rows the user explicitly searched the server
  // for on top of a search that already ran).
  const assigneeOptions = useMemo(() => {
    const q = assigneeSearch.trim().toLowerCase()
    const userOptions = users
      .filter((u) => {
        if (!q) return true
        const label = u.full_name || `${u.first_name} ${u.last_name}`
        return label.toLowerCase().includes(q)
      })
      .map((u) => ({ value: `user:${u.id}`, label: u.full_name || `${u.first_name} ${u.last_name}` }))

    const vendorOptions = accumulatedVendors.map((v) => ({
      value: `vendor:${v.id}`,
      label: `${v.name} (${t('labels.vendor')})`,
    }))

    const opts = [{ value: '', label: t('form.no_assignee') }, ...userOptions, ...vendorOptions]

    // Editing: the saved vendor may be outside the loaded/searched vendor
    // page. Without this the dropdown falls back to its placeholder and an
    // unrelated save would silently retarget the work order to a different
    // vendor (or clear the assignee outright). Users need no equivalent
    // guard: /api/users always returns the complete account list, so a saved
    // user id is never truncated by paging — only hidden by an in-progress
    // search, same as every other local-filter field in this file.
    const savedVendor = isEditing ? existingWorkOrder?.vendor : undefined
    if (savedVendor && !opts.some((o) => o.value === `vendor:${savedVendor.id}`)) {
      opts.splice(1, 0, { value: `vendor:${savedVendor.id}`, label: `${savedVendor.name} (${t('labels.vendor')})` })
    }

    return opts
  }, [users, accumulatedVendors, assigneeSearch, isEditing, existingWorkOrder, t])

  const maintenanceRequestOptions = useMemo(() => [
    { value: '', label: t('form.no_maintenance_request') },
    ...maintenanceRequests.map((mr) => ({ value: String(mr.id), label: `#${mr.id} - ${mr.title}` })),
  ], [maintenanceRequests, t])

  const priorityOptions = [
    { value: 'low', label: t('priorities.low') },
    { value: 'medium', label: t('priorities.medium') },
    { value: 'high', label: t('priorities.high') },
    { value: 'urgent', label: t('priorities.urgent') },
  ]

  const statusOptions = [
    { value: 'open', label: t('work_order_status.open') },
    { value: 'in_progress', label: t('work_order_status.in_progress') },
    { value: 'completed', label: t('work_order_status.completed') },
    { value: 'cancelled', label: t('work_order_status.cancelled') },
  ]

  // No client-side filtering here: the query runs on the server, so filtering
  // again locally would hide rows the user explicitly searched for.
  const equipmentOptions = useMemo(() => {
    const opts = [
      { value: '', label: t('form.no_equipment') },
      ...accumulatedEquipment.map((e) => ({ value: String(e.id), label: e.name })),
    ]
    // Editing: the saved equipment may be outside the loaded/searched page.
    // Without this the dropdown falls back to its placeholder and an unrelated
    // save would silently retarget the work order to different equipment.
    const savedEquipment = isEditing ? existingWorkOrder?.equipment : undefined
    if (savedEquipment && !opts.some((o) => o.value === String(savedEquipment.id))) {
      opts.splice(1, 0, { value: String(savedEquipment.id), label: savedEquipment.name })
    }
    return opts
  }, [accumulatedEquipment, isEditing, existingWorkOrder, t])

  // Label mirrors InvoiceFields' lease picker (`lease_code - customer (unit)`)
  // so a lease reads the same way across every form that links one.
  const leaseLabel = useCallback(
    (lease: Pick<Lease, 'lease_code'> & { customer?: { name: string }; unit?: { name: string } }) =>
      `${lease.lease_code} - ${lease.customer?.name || 'Unknown'} (${lease.unit?.name || 'Unknown'})`,
    []
  )

  // No client-side filtering here: the query runs on the server, so filtering
  // again locally would hide rows the user explicitly searched for.
  const leaseOptions = useMemo(() => {
    const opts = [
      { value: '', label: t('form.no_lease') },
      ...accumulatedLeases.map((l) => ({ value: String(l.id), label: leaseLabel(l) })),
    ]
    // Editing: the saved lease may be outside the loaded/searched page. The
    // edit-view blueprint only embeds {id, lease_code} (no customer/unit),
    // so this fallback entry is a plainer label than a freshly searched row.
    const savedLease = isEditing ? existingWorkOrder?.lease : undefined
    if (savedLease && !opts.some((o) => o.value === String(savedLease.id))) {
      opts.splice(1, 0, { value: String(savedLease.id), label: savedLease.lease_code })
    }
    // Deep-link: the settlement-card lease may also be outside the
    // loaded/searched page — this fetch DOES have customer/unit (GET
    // /api/leases/:id embeds both), so it gets the fuller label.
    if (deepLinkedLease && !opts.some((o) => o.value === String(deepLinkedLease.id))) {
      opts.splice(1, 0, { value: String(deepLinkedLease.id), label: leaseLabel(deepLinkedLease) })
    }
    return opts
  }, [accumulatedLeases, isEditing, existingWorkOrder, deepLinkedLease, leaseLabel, t])

  const {
    register,
    handleSubmit,
    reset,
    control,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<WorkOrderFormData>({
    defaultValues: {
      title: '',
      description: '',
      priority: 'medium',
      status: 'open',
      equipment_id: '',
      lease_id: deepLinkedLeaseId ? String(deepLinkedLeaseId) : '',
      assigned_to_id: '',
      maintenance_request_id: '',
      due_date: '',
      estimated_cost: '',
      actual_cost: '',
      notes: '',
    },
  })

  // Convert API assigned_to_id + vendor_id to combined value
  const toAssigneeValue = useCallback((wo: typeof existingWorkOrder) => {
    if (!wo) return ''
    if (wo.assigned_to_id) return `user:${wo.assigned_to_id}`
    if (wo.vendor_id) return `vendor:${wo.vendor_id}`
    return ''
  }, [])

  useEffect(() => {
    if (isEditing && existingWorkOrder) {
      reset({
        title: existingWorkOrder.title,
        description: existingWorkOrder.description || '',
        priority: existingWorkOrder.priority,
        status: existingWorkOrder.status,
        equipment_id: existingWorkOrder.equipment_id ? String(existingWorkOrder.equipment_id) : '',
        lease_id: existingWorkOrder.lease_id ? String(existingWorkOrder.lease_id) : '',
        assigned_to_id: toAssigneeValue(existingWorkOrder),
        maintenance_request_id: existingWorkOrder.maintenance_request_id
          ? String(existingWorkOrder.maintenance_request_id)
          : '',
        due_date: existingWorkOrder.due_date
          ? utcDateOnly(new Date(existingWorkOrder.due_date))
          : '',
        estimated_cost: existingWorkOrder.estimated_cost != null ? String(existingWorkOrder.estimated_cost) : '',
        actual_cost: existingWorkOrder.actual_cost != null ? String(existingWorkOrder.actual_cost) : '',
        notes: existingWorkOrder.notes || '',
      })
    }
  }, [isEditing, existingWorkOrder, reset, toAssigneeValue])

  const onSubmit = async (formData: WorkOrderFormData) => {
    // Parse combined assignee value
    let assigned_to_id: number | null = null
    let vendor_id: number | null = null
    if (formData.assigned_to_id.startsWith('user:')) {
      assigned_to_id = Number(formData.assigned_to_id.replace('user:', ''))
    } else if (formData.assigned_to_id.startsWith('vendor:')) {
      vendor_id = Number(formData.assigned_to_id.replace('vendor:', ''))
    }

    const payload = {
      title: formData.title,
      description: formData.description || undefined,
      priority: formData.priority,
      status: formData.status,
      // T130-001: either may be blank (cross-field-validated below, at least
      // one is required) — `Number('')` is 0, an invalid FK, so an unset
      // field must send `null`, never fall through to 0.
      equipment_id: formData.equipment_id ? Number(formData.equipment_id) : null,
      lease_id: formData.lease_id ? Number(formData.lease_id) : null,
      assigned_to_id,
      vendor_id,
      maintenance_request_id: formData.maintenance_request_id
        ? Number(formData.maintenance_request_id)
        : null,
      due_date: formData.due_date || undefined,
      estimated_cost: formData.estimated_cost ? Number(formData.estimated_cost) : null,
      actual_cost: formData.actual_cost ? Number(formData.actual_cost) : null,
      notes: formData.notes || undefined,
      image_urls: imageUrls,
    }

    if (isEditing) {
      await updateWorkOrder.mutateAsync({ id: workOrderId, ...payload })
      navigate(`/work-orders/${workOrderId}`)
    } else {
      const result = await createWorkOrder.mutateAsync(payload)
      navigate(`/work-orders/${result.id}`)
    }
  }

  if (isEditing && isLoadingWorkOrder) {
    return (
      <div className="space-y-6">
        <Card><div className="h-64 animate-pulse bg-base-200 rounded" /></Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('work_orders.title'), href: '/work-orders' },
          { label: isEditing ? t('work_orders.edit') : t('work_orders.new') },
        ]}
      />

      <PageHeader
        title={isEditing ? t('form.title_edit_work_order') : t('form.title_new_work_order')}
        description={isEditing ? t('form.description_edit_work_order') : t('form.description_new_work_order')}
      />

      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="md:col-span-2">
              <Input
                label={t('labels.title')}
                error={errors.title?.message}
                {...register('title', { required: tc('errors.required') })}
              />
            </div>
            <Select
              label={t('labels.priority')}
              options={priorityOptions}
              {...register('priority')}
            />
            {isEditing && (
              <Select
                label={t('labels.status')}
                options={statusOptions}
                {...register('status')}
              />
            )}
            {/*
              T130-001/T130-013: equipment and lease are no longer independently
              required — the model (and the validate rule below) only requires
              at least ONE of the two. Neither field carries its own `required`
              asterisk or its own error message; a single shared hint line
              covers the combined rule (rendered right after the Lease field
              below) so the message doesn't imply either field alone is
              mandatory.
            */}
            <Controller
              name="equipment_id"
              control={control}
              rules={{
                validate: (value) => !!value || !!getValues('lease_id') || t('form.equipment_or_lease_hint'),
              }}
              render={({ field }) => (
                <SearchableDropdown
                  label={t('labels.equipment')}
                  placeholder={t('form.no_equipment')}
                  options={equipmentOptions}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value)
                    void trigger('lease_id')
                  }}
                  onSearch={setEquipmentSearch}
                  isLoading={isLoadingEquipment}
                  hasMore={
                    !!equipmentData?.meta &&
                    equipmentData.meta.current_page < equipmentData.meta.total_pages
                  }
                  onLoadMore={() => setEquipmentPage((p) => p + 1)}
                  name="equipment_id"
                />
              )}
            />
            {showLeasePicker && (
              <Controller
                name="lease_id"
                control={control}
                rules={{
                  validate: (value) => !!value || !!getValues('equipment_id') || t('form.equipment_or_lease_hint'),
                }}
                render={({ field }) => (
                  <SearchableDropdown
                    label={t('labels.lease')}
                    placeholder={t('form.no_lease')}
                    options={leaseOptions}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value)
                      void trigger('equipment_id')
                    }}
                    onSearch={setLeaseSearch}
                    isLoading={isLoadingLeases}
                    hasMore={
                      !!leasesData?.meta && leasesData.meta.current_page < leasesData.meta.total_pages
                    }
                    onLoadMore={() => setLeasePage((p) => p + 1)}
                    disabled={!!deepLinkedLeaseId}
                    hint={deepLinkedLeaseId ? t('form.lease_locked_caption') : undefined}
                    name="lease_id"
                  />
                )}
              />
            )}
            {(errors.equipment_id || errors.lease_id) && (
              <p className="md:col-span-2 text-sm text-error" data-testid="equipment-or-lease-hint">
                {t('form.equipment_or_lease_hint')}
              </p>
            )}
            <Controller
              name="assigned_to_id"
              control={control}
              render={({ field }) => (
                <SearchableDropdown
                  label={t('labels.assigned_to')}
                  placeholder={t('form.no_assignee')}
                  options={assigneeOptions}
                  value={field.value}
                  onChange={field.onChange}
                  onSearch={setAssigneeSearch}
                  isLoading={isLoadingVendors}
                  hasMore={
                    !!vendorsData?.meta &&
                    vendorsData.meta.current_page < vendorsData.meta.total_pages
                  }
                  onLoadMore={() => setVendorPage((p) => p + 1)}
                  name="assigned_to_id"
                />
              )}
            />
            <Controller
              name="maintenance_request_id"
              control={control}
              render={({ field }) => (
                <SearchableDropdown
                  label={t('labels.maintenance_request')}
                  placeholder={t('form.no_maintenance_request')}
                  options={maintenanceRequestOptions}
                  value={field.value}
                  onChange={field.onChange}
                  name="maintenance_request_id"
                />
              )}
            />
            <Input
              label={t('labels.due_date')}
              type="date"
              {...register('due_date')}
            />
            <Input
              label={t('labels.estimated_cost')}
              type="number"
              min={0}
              step="0.01"
              {...register('estimated_cost')}
            />
            {isEditing && (
              <Input
                label={t('labels.actual_cost')}
                type="number"
                min={0}
                step="0.01"
                {...register('actual_cost')}
              />
            )}
          </div>

          <Textarea
            label={t('labels.description')}
            rows={3}
            {...register('description')}
          />
          <Textarea
            label={t('labels.notes')}
            rows={2}
            {...register('notes')}
          />

          <FileUpload
            label={t('labels.images')}
            value={imageUrls}
            onChange={setImageUrls}
            maxFiles={10}
            accept="image/*"
            onUpload={uploadWorkOrderImage}
          />

          <FormActions>
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              type="submit"
              loading={isEditing ? updateWorkOrder.isPending : createWorkOrder.isPending}
            >
              {isEditing ? tc('actions.save_changes') : tc('actions.create')}
            </Button>
          </FormActions>
        </form>
      </Card>
    </div>
  )
}
